import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import type { PartnerConfig, EventConfig, ResolvedConfig } from './types';

type Status = 'loading' | 'found' | 'not_found';

interface UsePartnerConfigResult {
  status: Status;
  config: ResolvedConfig | null;
}

export function usePartnerConfig(
  partnerId: string,
  eventId?: string
): UsePartnerConfigResult {
  const [status, setStatus] = useState<Status>('loading');
  const [config, setConfig] = useState<ResolvedConfig | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const partnerSnap = await getDoc(
          doc(db, 'partners', partnerId)
        );

        if (!partnerSnap.exists()) {
          if (!cancelled) setStatus('not_found');
          return;
        }

        const partner = partnerSnap.data() as PartnerConfig;

        if (!partner.isActive) {
          if (!cancelled) setStatus('not_found');
          return;
        }

        let event: Partial<EventConfig> = {};
        if (eventId) {
          const eventSnap = await getDoc(
            doc(db, 'partners', partnerId, 'events', eventId)
          );
          if (eventSnap.exists()) {
            const eventData = eventSnap.data() as EventConfig;
            if (eventData.isActive) {
              event = eventData;
            }
          }
        }

        const resolved: ResolvedConfig = {
          partnerId,
          eventId,
          name: partner.name,
          logoUrl: partner.logoUrl,
          brandColor: partner.brandColor,
          buttonTextColor: partner.buttonTextColor ?? '#ffffff',
          landingHeadline: event.landingHeadline ?? partner.landingHeadline,
          landingSubtext: event.landingSubtext ?? partner.landingSubtext,
        };

        if (!cancelled) {
          setConfig(resolved);
          setStatus('found');
        }
      } catch {
        if (!cancelled) setStatus('not_found');
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [partnerId, eventId]);

  return { status, config };
}
